import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listSessions, revokeSession, revokeOtherSessions, type SessionSummary } from '../api';
import { describeDevice, relativeTime } from '../userAgent';
import { QueryState } from './QueryState';
import { useAccountDay } from '../accountDay';
import { readableInstant } from '../dateLabels';

/**
 * Where you are signed in.
 *
 * All three endpoints — list, end one, end all the others — have existed since Phase 2, are
 * tested, and were reachable from nowhere in the application. The API's own schema carries a
 * `current` flag with the comment "so the UI can say 'this device'", written for a UI that was
 * never built.
 *
 * The consequence was not cosmetic. Signing in on a friend's laptop or a machine you then lost
 * left a session valid for its full lifetime with no way to end it: "Sign out" ends the session
 * you are holding, and that is the one session you do not need to worry about. The account's own
 * settings screen offered to revoke API tokens — the least likely thing to go missing — while the
 * browser sessions, which is what actually walks out of the building, could not be listed.
 */

function SessionRow({ session }: { session: SessionSummary }) {
  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: () => revokeSession(session.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const device = describeDevice(session.userAgent);
  /* The account's clock, not this laptop's — see `dateLabels.ts`. */
  const { timezone } = useAccountDay();

  return (
    <li className={`session-row ${session.current ? 'is-current' : ''}`}>
      <div>
        <strong>{device ?? 'Unrecognised device'}</strong>{' '}
        {session.current && <span className="pill">this device</span>}
        {/* The raw string, for the row you do not recognise and want to look at properly. */}
        {session.userAgent && (
          <span className="subtext session-ua" title={session.userAgent}>
            {session.userAgent}
          </span>
        )}
      </div>
      <div className="muted token-meta">
        <span title={readableInstant(session.lastSeenAt, timezone)}>
          last used {relativeTime(session.lastSeenAt)}
        </span>
        {' · '}
        <span title={readableInstant(session.createdAt, timezone)}>
          signed in {relativeTime(session.createdAt)}
        </span>
        {' · '}
        expires {readableInstant(session.expiresAt, timezone, 'day')}
        {/* Ending the session you are using is just signing out, and the button for that is in
            the header. Offering it here as well only invites doing it by accident while trying
            to get rid of a different one. */}
        {!session.current && (
          <button
            className="link danger"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
          >
            sign out
          </button>
        )}
      </div>
    </li>
  );
}

interface DeviceGroup {
  device: string;
  sessions: SessionSummary[];
  hasCurrent: boolean;
}

/**
 * Sessions gathered by the device they claim to be, current first.
 *
 * A device with one session is the interesting row — an unfamiliar browser that signed in once —
 * so after the current device the groups are ordered by size ascending, which puts the odd one out
 * at the top rather than under thirty of your own.
 */
function byDevice(sessions: readonly SessionSummary[]): DeviceGroup[] {
  const map = new Map<string, SessionSummary[]>();
  for (const s of sessions) {
    const key = describeDevice(s.userAgent) ?? 'Unrecognised device';
    map.set(key, [...(map.get(key) ?? []), s]);
  }
  return [...map]
    .map(([device, list]) => ({
      device,
      sessions: list,
      hasCurrent: list.some((s) => s.current),
    }))
    .sort((a, b) => {
      if (a.hasCurrent !== b.hasCurrent) return a.hasCurrent ? -1 : 1;
      return a.sessions.length - b.sessions.length;
    });
}

/** One device, with its sessions behind a count until you ask. */
function DeviceGroup({ group }: { group: DeviceGroup }) {
  /* A single session has nothing to fold, so it is drawn open and has no control. */
  const foldable = group.sessions.length > 1;
  const [open, setOpen] = useState(!foldable);
  const latest = group.sessions.reduce((a, b) => (a.lastSeenAt > b.lastSeenAt ? a : b));

  return (
    <li className="session-device" data-testid="session-device" data-device={group.device}>
      <div className="session-device-head">
        <strong>{group.device}</strong>{' '}
        {group.hasCurrent && <span className="pill">this device</span>}{' '}
        <span className="subtext">
          {group.sessions.length === 1
            ? `last used ${relativeTime(latest.lastSeenAt)}`
            : `${group.sessions.length} sessions · last used ${relativeTime(latest.lastSeenAt)}`}
        </span>{' '}
        {foldable && (
          <button
            type="button"
            className="link"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'hide' : 'show each'}
          </button>
        )}
      </div>
      {open && (
        <ul className="plain">
          {group.sessions.map((s) => (
            <SessionRow key={s.id} session={s} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function Sessions() {
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: listSessions });
  const revokeRest = useMutation({
    mutationFn: revokeOtherSessions,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const others = (sessions.data ?? []).filter((s) => !s.current).length;

  const groups = useMemo(() => byDevice(sessions.data ?? []), [sessions.data]);

  return (
    <section>
      <h2>Signed in on</h2>
      <p className="muted">
        Every browser currently holding a session for this account. Ending one signs that browser
        out immediately; it does not change your password, so anyone who knows it can sign in again.
      </p>

      {/*
        Grouped by device, and folded.

        Twenty-nine rows, each carrying its full user-agent string, made the session list about
        40% of the whole Settings screen — and every row said the same thing, so the list that
        exists to answer "is one of these not me" could not be scanned for the one that was not.
        The device is the unit somebody recognises; the individual sessions matter only once you
        have found the device you do not know.

        The current device leads, then the noisiest group: an unfamiliar device with one session
        is exactly what you are looking for, and it should not be below thirty rows of your own.
      */}
      <QueryState query={sessions} />
      <ul className="session-list" data-testid="session-list">
        {groups.map((group) => (
          <DeviceGroup key={group.device} group={group} />
        ))}
      </ul>

      {others > 0 && (
        <div className="detail-foot">
          <button
            type="button"
            className="danger"
            disabled={revokeRest.isPending}
            onClick={() => revokeRest.mutate()}
          >
            Sign out of {others} other {others === 1 ? 'browser' : 'browsers'}
          </button>
        </div>
      )}
      {others === 0 && sessions.data && (
        <p className="subtext">This is the only browser signed in.</p>
      )}
    </section>
  );
}
